import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMoney, nextDueDate, formatDate, type CreditCustomer } from '@/lib/credit';
import { downloadCSV } from '@/lib/csv';

interface Props {
  customers: CreditCustomer[];
  balances: Record<number, number>;
  currency: string;
  lastActivity: Record<number, string | null>;
  onNewCredit: (c: CreditCustomer) => void;
  onPayment: (c: CreditCustomer) => void;
  onEdit: (c: CreditCustomer) => void;
  onDelete: (c: CreditCustomer) => void;
  onInvite: (c: CreditCustomer) => void;
  onCredentials: (c: CreditCustomer) => void;
  onQr: (c: CreditCustomer) => void;
}

export default function ClientsView({
  customers, balances, currency, lastActivity, onNewCredit, onPayment, onEdit, onDelete, onInvite, onCredentials, onQr,
}: Props) {
  const { t } = useTranslation();
  const nameOf = (c: CreditCustomer) => c.full_name || 'Client';
  const exportCSV = () => {
    downloadCSV(
      'clients-credit.csv',
      ['Nom', 'Téléphone', 'Solde', 'Plafond', 'Dernière activité', 'Notes', 'Devise'],
      customers.map((c) => [
        nameOf(c),
        c.phone || '',
        String(balances[c.id] || 0),
        c.credit_limit != null ? String(c.credit_limit) : '',
        lastActivity[c.id] ? formatDate(lastActivity[c.id]) : '',
        c.notes || '',
        currency,
      ])
    );
  };

  if (customers.length === 0) {
    return (
      <div className="py-16 text-center bg-background-50 border border-background-200/70 rounded-lg">
        <i className="ri-user-line text-3xl text-foreground-300"></i>
        <p className="mt-3 text-sm text-foreground-500">{t('crf.no_customer', { defaultValue: 'Aucun client enregistré' })}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
    <div className="flex justify-end">
      <button onClick={exportCSV}
        className="flex items-center gap-1.5 px-4 py-2 bg-background-50 border border-background-200/70 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
        <i className="ri-download-2-line"></i>{t('crf.export_csv', { defaultValue: 'Exporter CSV' })}
      </button>
    </div>
    <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
      <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-3 border-b border-background-200/70 bg-background-100 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">
        <div className="col-span-3">{t('crf.col_client', { defaultValue: 'Client' })}</div>
        <div className="col-span-2">{t('crf.col_balance', { defaultValue: 'Solde' })}</div>
        <div className="col-span-2">{t('crf.col_limit', { defaultValue: 'Plafond / dispo.' })}</div>
        <div className="col-span-2">{t('crf.col_due', { defaultValue: 'Échéance' })}</div>
        <div className="col-span-3 text-right">{t('crf.col_actions', { defaultValue: 'Actions' })}</div>
      </div>

      <div className="divide-y divide-background-200/70">
        {customers.map((c) => {
          const bal = balances[c.id] || 0;
          const available = c.credit_limit != null ? Math.max(0, c.credit_limit - bal) : null;
          const due = nextDueDate(c.usual_payment_date);
          return (
            <div key={c.id} className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-3 px-4 py-3.5 items-center">
              <div className="lg:col-span-3 min-w-0">
                <Link to={`/dashboard/credit/customer/${c.id}`} className="flex items-center gap-2.5 no-underline group">
                  <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {nameOf(c).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-700 transition-colors">{nameOf(c)}</div>
                    <div className="text-xs text-foreground-400 truncate">
                      {c.linked_user_id ? <><i className="ri-link text-[10px]"></i> {t('crf.linked_account', { defaultValue: 'Compte lié' })} · </> : ''}{c.phone || t('crf.no_phone', { defaultValue: 'Sans téléphone' })}
                    </div>
                  </div>
                </Link>
              </div>

              <div className="lg:col-span-2">
                <span className={`text-sm font-bold ${bal > 0 ? 'text-primary-700' : 'text-accent-600'}`}>{formatMoney(bal, currency)}</span>
                {lastActivity[c.id] && <div className="text-[10px] text-foreground-400">{t('crf.last_activity', { defaultValue: 'dernier' })} : {formatDate(lastActivity[c.id])}</div>}
              </div>

              <div className="lg:col-span-2">
                {c.credit_limit != null ? (
                  <>
                    <div className="text-xs text-foreground-700">{formatMoney(c.credit_limit, currency)}</div>
                    <div className="text-[10px] text-foreground-400">{t('crf.available_short', { defaultValue: 'dispo.' })} {formatMoney(available ?? 0, currency)}</div>
                  </>
                ) : (
                  <span className="text-xs text-foreground-400">{t('crf.no_limit', { defaultValue: 'Aucun plafond' })}</span>
                )}
              </div>

              <div className="lg:col-span-2">
                <span className={`text-xs ${due && bal > 0 ? 'text-foreground-800 font-medium' : 'text-foreground-400'}`}>
                  {due ? formatDate(due) : '—'}
                </span>
              </div>

              <div className="lg:col-span-3 flex flex-wrap items-center gap-1.5 lg:justify-end">
                <button onClick={() => onNewCredit(c)} title={t('crf.tooltip_add_credit', { defaultValue: 'Ajouter un crédit' })} className="w-8 h-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                  <i className="ri-add-line text-sm"></i>
                </button>
                <button onClick={() => onPayment(c)} title={t('crf.tooltip_payment', { defaultValue: 'Paiement' })} className="w-8 h-8 rounded-full bg-accent-50 text-accent-700 flex items-center justify-center hover:bg-accent-100 transition-colors cursor-pointer">
                  <i className="ri-hand-coin-line text-sm"></i>
                </button>
                <button onClick={() => onInvite(c)} title={t('crf.tooltip_invite', { defaultValue: 'Inviter sur Zifek' })} className="w-8 h-8 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer">
                  <i className="ri-user-follow-line text-sm"></i>
                </button>
                <button onClick={() => onCredentials(c)} title={t('crf.tooltip_credentials', { defaultValue: 'Voir les identifiants' })} className="w-8 h-8 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer">
                  <i className="ri-key-line text-sm"></i>
                </button>
                <button onClick={() => onQr(c)} title={t('crf.qr_client', { defaultValue: 'QR client' })} className="w-8 h-8 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer">
                  <i className="ri-qr-code-line text-sm"></i>
                </button>
                <button onClick={() => onEdit(c)} title={t('crf.tooltip_edit', { defaultValue: 'Modifier' })} className="w-8 h-8 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer">
                  <i className="ri-edit-line text-sm"></i>
                </button>
                <button onClick={() => onDelete(c)} title={t('crf.tooltip_delete', { defaultValue: 'Supprimer' })} className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors cursor-pointer">
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
}